// The browser half of shift alerts (shift-alerts-info-tab AC3, AC4, AC5, AC7,
// AC8). The pure functions decide which state the Info card is in from facts
// the browser reports; the async ones do the three things a phone can do —
// register the worker, subscribe, unsubscribe. Nothing here talks to the API.

export type AlertsState = 'install' | 'ready' | 'on' | 'blocked' | 'unsupported';

export interface AlertsFacts {
  supported: boolean;
  permission: NotificationPermission;
  needsInstall: boolean;
  subscribed: boolean;
}

// iPhone and iPad Safari deliver push only to an app on the Home Screen. Any
// other browser pushes from the tab. (iPadOS can present as a Mac; that iPad
// would see Ready and Safari would refuse the subscribe — the card's error
// line covers it rather than a UA heuristic nobody can keep right.)
export function needsHomeScreen(userAgent: string, standalone: boolean): boolean {
  return /iPhone|iPad|iPod/.test(userAgent) && !standalone;
}

// Denied beats everything: only Settings can undo it. Then the install step,
// which on an iPhone comes BEFORE the browser can even report PushManager.
export function alertsStateFor(f: AlertsFacts): AlertsState {
  if (f.permission === 'denied') return 'blocked';
  if (f.needsInstall) return 'install';
  if (!f.supported) return 'unsupported';
  if (f.permission === 'granted' && f.subscribed) return 'on';
  return 'ready';
}

export function uint8ArrayToUrlBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The VAPID key this subscription was made under, as the server hands keys out; null when the browser does not expose it. */
export function subscriptionKey(sub: PushSubscription | null): string | null {
  const k = sub?.options?.applicationServerKey;
  return k ? uint8ArrayToUrlBase64(new Uint8Array(k)) : null;
}

/**
 * On means THIS phone under the CURRENT key: the subscription this browser
 * holds is the endpoint on file AND was made with the server's VAPID key. A
 * phone replaced by another reads Ready; so does one left behind by a key
 * rotation, whose every send would be refused and whose subscribe() would
 * throw on the mismatch (review, PR #278, rounds 2 and 6). A browser that
 * does not expose the key is judged on the endpoint alone.
 */
export function subscriptionIsCurrent(local: { endpoint: string | null; key: string | null }, server: { endpoint: string | null; publicKey: string | null }): boolean {
  if (!local.endpoint || !server.endpoint || local.endpoint !== server.endpoint) return false;
  return local.key === null || server.publicKey === null || local.key === server.publicKey;
}

export function urlBase64ToUint8Array(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// --- browser calls ---------------------------------------------------------

export const SW_PATH = '/sw.js';

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || (window.matchMedia?.('(display-mode: standalone)').matches ?? false);
}

export function permissionNow(): NotificationPermission {
  return typeof Notification === 'undefined' ? 'default' : Notification.permission;
}

export async function registerWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    await navigator.serviceWorker.register(SW_PATH);
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registerWorker();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// Ask the phone (the OS prompt is the phone's own), then subscribe with the
// server's VAPID key. Throws when the phone refuses — the caller re-reads the
// permission and the card moves to Blocked.
export async function subscribePush(publicKey: string): Promise<PushSubscriptionJSON> {
  const reg = await registerWorker();
  if (!reg) throw new Error('no service worker');
  const key = urlBase64ToUint8Array(publicKey);
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key.buffer as ArrayBuffer });
  return sub.toJSON();
}

// The card re-reads when the app comes back to the front (review, PR #278,
// round 5): a phone replaced while this one was in the background would
// otherwise keep reading On from the facts it mounted with. Returns the
// disposer. `doc` is a parameter so the contract is unit-tested without a DOM.
export function onVisible(
  doc: { visibilityState: string; addEventListener: (t: 'visibilitychange', fn: () => void) => unknown; removeEventListener: (t: 'visibilitychange', fn: () => void) => unknown },
  fn: () => void,
): () => void {
  const handler = () => {
    if (doc.visibilityState === 'visible') fn();
  };
  doc.addEventListener('visibilitychange', handler);
  return () => doc.removeEventListener('visibilitychange', handler);
}

// `unavailable` is the server's own answer (503 `unavailable`: the VAPID
// secrets are not set) — nothing about this phone is unknown, the kennel's
// setup is unfinished, and the card must say that rather than blame the phone
// (Freddi, 2026-10-23: prod read "Couldn't check this phone" for a week).
export type ServerRead = 'pending' | 'ready' | 'failed' | 'unavailable';

/** What a failed server read means for the card: the server's own refusal, or a read that never landed. */
export function readAfter(err: unknown): 'failed' | 'unavailable' {
  const e = err as { status?: unknown; code?: unknown } | null | undefined;
  return e && e.status === 503 && e.code === 'unavailable' ? 'unavailable' : 'failed';
}

// The card leaves Checking… only when BOTH reads have landed: the server's
// answer alone, on a phone already subscribed, would render Ready with a live
// Turn on until the browser's facts arrive (review, PR #278, round 8). The
// preview has no browser to wait for. A failed server read is failed.
export function cardRead(server: ServerRead, browserDone: boolean, preview: boolean): ServerRead {
  if (server !== 'ready') return server;
  return preview || browserDone ? 'ready' : 'pending';
}

// Wrap an async read so that only the LATEST call's answer comes back: a
// superseded call resolves undefined whatever its outcome, so a slow older
// read (a mount, an action) cannot land after a newer one and put the facts
// back (review, PR #278, round 7).
export function latestOnly<A extends unknown[], T>(fn: (...args: A) => Promise<T>): (...args: A) => Promise<T | undefined> {
  let gen = 0;
  return async (...args: A) => {
    const mine = ++gen;
    try {
      const r = await fn(...args);
      return mine === gen ? r : undefined;
    } catch (e) {
      if (mine === gen) throw e;
      return undefined;
    }
  };
}

export async function unsubscribePush(): Promise<void> {
  const sub = await currentSubscription();
  if (sub) await sub.unsubscribe().catch(() => undefined);
}
